// LOS FILTROS DE RENOVACIONES, Y CÓMO SE QUITAN.
//
// Lissette, 2026-09-23: *"en renovaciones cuando le coloco filtro me desaparece
// todo tengo que recargar para que vuelvan a aparecer"*, y con la captura
// delante: *"no me queda como la barra de busqueda para eliminar el filtro que
// tenia, y volver a todos los clientes sin refrescar la pagina"*.
//
// ── LO QUE PASABA ───────────────────────────────────────────────────────────
//
// La tabla se pintaba así:
//
//     {filtered.length === 0 ? <mensaje/> : <tabla/>}
//
// y los filtros —Compañía, Status, Revisión, Encargado, la búsqueda por nombre
// y las columnas propias de la agencia— viven TODOS dentro de la CABECERA de
// esa tabla. Así que en cuanto un filtro dejaba la lista en cero, desaparecía
// la tabla, y con ella el control que acababa de usarse. No quedaba nada que
// tocar: la única salida era recargar la página, que borra el estado de React
// y con él los filtros.
//
// El mensaje estaba pensado —dice cuál de los cuatro vacíos es, por la regla de
// oro 89— pero pensar QUÉ decir no es lo mismo que dejar por dónde salir.
//
// ── POR QUÉ ESTA LISTA VIVE AQUÍ ────────────────────────────────────────────
//
// Porque el arreglo tiene una forma exacta de romperse: se añade un filtro
// séptimo a la pantalla y se olvida meterlo en "quitar los filtros". Ese día la
// tabla vuelve a quedarse sin salida, y sólo para quien use ese filtro. La
// lista está aquí, en un `.js`, para que una prueba pueda comprobar que el
// botón los toca TODOS (regla 48: lo que no se puede importar no se puede
// comprobar).

/** El valor de un desplegable sin filtrar. */
export const SIN_FILTRO = 'all';

/**
 * TODOS los filtros de la pantalla de Renovaciones.
 *
 * `vacio` es el valor que significa "sin filtrar" para cada uno. Añadir un
 * filtro a la pantalla es añadirlo aquí; si no, "Quitar los filtros" lo dejará
 * puesto y la tabla seguirá vacía después de pulsarlo.
 */
export const FILTROS_DE_RENOVACIONES = [
  { id: 'typeFilter', es: 'Tipo de póliza', en: 'Policy type', vacio: SIN_FILTRO },
  { id: 'carrierFilter', es: 'Compañía', en: 'Company', vacio: SIN_FILTRO },
  { id: 'stageFilter', es: 'Status', en: 'Status', vacio: SIN_FILTRO },
  { id: 'encargadoFilter', es: 'Encargado', en: 'Assignee', vacio: SIN_FILTRO },
  // Quién atiende al cliente en su ficha (Lissette 2026-09-30).
  { id: 'atiendeFilter', es: 'Atiende', en: 'Handled by', vacio: SIN_FILTRO },
  { id: 'reviewFilter', es: 'Revisión', en: 'Review', vacio: SIN_FILTRO },
  { id: 'clientFilter', es: 'Búsqueda por nombre', en: 'Name search', vacio: '' },
];

/** La clave del objeto donde viven los filtros de las columnas propias. */
export const FILTROS_PROPIOS = 'filtrosPropios';

/**
 * Los filtros que están puestos ahora mismo, con su nombre para enseñarlo.
 *
 * Que se ENSEÑEN es parte del arreglo: el vacío decía "ningún resultado con
 * esos filtros" sin decir cuáles, y con seis repartidos por las cabeceras de la
 * tabla eso obliga a repasarlos uno a uno.
 *
 * @param {object} estado  { typeFilter, carrierFilter, …, filtrosPropios }
 * @param {object} nombresDeColumna  { [idDeColumnaPropia]: 'ID.me' }
 * @param {'es'|'en'} lang
 * @returns {Array<{id: string, etiqueta: string}>}
 */
export function filtrosPuestos(estado, nombresDeColumna = {}, lang = 'es') {
  const es = lang !== 'en';
  const e = estado || {};
  const out = [];
  for (const f of FILTROS_DE_RENOVACIONES) {
    const valor = e[f.id];
    // El de texto se compara TRIMEADO: un espacio suelto en la búsqueda no
    // encuentra a nadie y no se vería como filtro puesto.
    const puesto = f.vacio === '' ? String(valor ?? '').trim() !== '' : (valor != null && valor !== f.vacio);
    if (puesto) out.push({ id: f.id, etiqueta: es ? f.es : f.en });
  }
  for (const [idCol, valor] of Object.entries(e[FILTROS_PROPIOS] || {})) {
    if (valor === undefined || valor === null || valor === '') continue;
    out.push({ id: `${FILTROS_PROPIOS}.${idCol}`, etiqueta: nombresDeColumna[idCol] || idCol });
  }
  return out;
}

/** ¿Hay algún filtro puesto? */
export function hayFiltrosPuestos(estado, nombresDeColumna = {}) {
  return filtrosPuestos(estado, nombresDeColumna).length > 0;
}

/**
 * Los valores a los que hay que volver para quitarlos todos.
 *
 * Se devuelven en vez de aplicarse porque quien los aplica es React, con sus
 * `setX`. Lo que importa —y lo que se puede probar— es que la lista esté
 * COMPLETA.
 */
export function valoresSinFiltro() {
  const out = {};
  for (const f of FILTROS_DE_RENOVACIONES) out[f.id] = f.vacio;
  out[FILTROS_PROPIOS] = {};
  return out;
}
